import {
  Box, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, InputLabel,
  MenuItem, Select, SelectChangeEvent, TextField, Typography, CircularProgress,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, List, ListItem, ListItemIcon, ListItemText
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState, useEffect, useCallback, useRef } from "react";
import { useBlocker } from "react-router-dom";
import ActivityDetails from "./ActivityDetails";
import { DailyReport, DailyReportActivity, DailyReportTower, DailyReportEmployee, DailyReportEquipment } from "../types/dailyReportTypes";
import { DailyReportSubcontract } from "../types/subcontractTypes";
import { ProjectActivity } from "../types/ActivityTypes";
import { getProjectActivities } from "../api/activityApi";
import { Employee } from "../types/EmployeeTypes";
import { getEmployeesByIdProject, getEmployeesByReportDate, getFichadosByDate } from "../api/employeeApi";
import { Equipment } from "../types/equipmentTypes";
import { getEquipmentByIdProject, getEquipmentsByReportDate } from "../api/equipmentApi";
import { Tower } from "../types/towerTypes";
import { getTowersByProjectId } from "../api/towerApi";
import { getLastDailyReport } from "../api/dailyReportApi";
import { buildActivityOrderMap, sortActivitiesByProjectOrder } from "../utils/activityOrder";
import { Project } from "../types/projectTypes";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useCountry } from "../contexts/CountryContext";
import { ProjectActivityTowerResponse } from "../types/projectActivityTowerTypes";
import { getActivityTowerRelationsByProject } from "../api/projectActivityTowerApi";

const normalizeText = (text: string | undefined | null) => {
  if (!text) return "";
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

/**
 * Nombre con que se identifica a un colaborador en los avisos de validacion.
 *
 * El nombre de pila solo no basta para distinguir a dos personas del mismo frente, asi que se
 * muestra con el apellido paterno. El apellido proviene del personal sincronizado de BUK: si la
 * marca no calza con la nomina de esa fecha solo se conoce el documento, y entonces se identifica
 * por RUT en vez de dejar el aviso sin referencia.
 */
const nombrePersonal = (emp: Employee): string =>
  [emp.name, emp.firstName].filter(Boolean).join(" ").trim() || emp.rut || "Sin identificar";

/**
 * Etiqueta de un equipo en los avisos de validacion.
 *
 * La patente va primero porque es como se reconoce al equipo en obra; el tipo la acompania para
 * los casos en que la patente no diga nada por si sola.
 */
const etiquetaEquipo = (eq: Equipment): string =>
  [eq.vehicleNumber, eq.name].filter(Boolean).join(" / ") || `Equipo ${eq.id}`;

const getForwardSpan = (towerObj: any): number => {
  if (!towerObj) return 0;
  if (towerObj.forwardSpanM) return Number(towerObj.forwardSpanM);
  if (towerObj.forward_span_m) return Number(towerObj.forward_span_m);
  if (towerObj.forwardSpan) return Number(towerObj.forwardSpan);

  const key = Object.keys(towerObj).find(k =>
      k.toLowerCase().includes('forward') ||
      k.toLowerCase().includes('span') ||
      k.toLowerCase().includes('vano')
  );
  if (key) return Number(towerObj[key]);
  return 0;
};

const distributeArray = <T,>(arr: T[], numChunks: number): T[][] => {
  const result: T[][] = Array.from({ length: numChunks }, () => []);
  arr.forEach((item, index) => {
    result[index % numChunks].push(item);
  });
  return result;
};

const distributeProportionally = <T,>(items: T[], weights: number[]): T[][] => {
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const numChunks = weights.length;

  if (totalWeight === 0 || items.length === 0) return distributeArray(items, numChunks);

  const exactCounts = weights.map(w => (w / totalWeight) * items.length);
  const intCounts = exactCounts.map(Math.floor);
  let unassignedCount = items.length - intCounts.reduce((a, b) => a + b, 0);

  const remainders = exactCounts.map((val, idx) => ({ idx, rem: val - intCounts[idx] }));
  remainders.sort((a, b) => b.rem - a.rem);

  for (let i = 0; i < unassignedCount; i++) {
    intCounts[remainders[i].idx]++;
  }

  const result: T[][] = [];
  let currentIndex = 0;
  for (let i = 0; i < numChunks; i++) {
    const count = intCounts[i];
    result.push(items.slice(currentIndex, currentIndex + count));
    currentIndex += count;
  }
  return result;
};

const rebuildMatrixActivities = (rawActivities: any[]) => {
  const pivotedActivities: any[] = [];
  const processedMatrixGroups = new Set<string>();

  rawActivities.forEach((act) => {
    const actName = act.activityName?.trim() || act.activity?.name?.trim() || "";
    const isMatrix = actName === "Riega de Piloto Conductor" || actName === "Tendido de Conductor";

    if (!isMatrix) {
      pivotedActivities.push(act);
      return;
    }

    if (processedMatrixGroups.has(actName)) return;
    processedMatrixGroups.add(actName);

    const relatedPhases = rawActivities.filter(a =>
      (a.activityName?.trim() === actName || a.activity?.name?.trim() === actName)
    );

    const mergedActivity = JSON.parse(JSON.stringify(relatedPhases[0]));

    mergedActivity._phaseActivityIds = {};

    if (mergedActivity.activity) {
        mergedActivity.activity.subActivity = "";
    }

    const mergedFrontsMap = new Map<number, any>();

    relatedPhases.forEach(phaseAct => {
      const phaseName = phaseAct.activity?.subActivity || "";

      if (phaseName) {
          mergedActivity._phaseActivityIds[phaseName] = phaseAct.id || 0;
      }

      (phaseAct.workFronts || []).forEach((front: any) => {
        if (!mergedFrontsMap.has(front.frontNumber)) {
          mergedFrontsMap.set(front.frontNumber, {
            ...front,
            personnelRows: [],
            equipmentRows: [],
            productionRows: [],
            subcontractRows: [],
            _towerMap: new Map<string, any>(),
            _personnelMap: new Map<string, any>(),
            _equipmentMap: new Map<number, any>(),
            _subcontractMap: new Map<string, any>()
          });
        }

        const mergedFront = mergedFrontsMap.get(front.frontNumber);

        (front.personnelRows || []).forEach((p: any) => {
          const empId = p.employee?.rut;
          if (empId && !mergedFront._personnelMap.has(empId)) {
            mergedFront._personnelMap.set(empId, p);
            mergedFront.personnelRows.push(p);
          }
        });

        (front.equipmentRows || []).forEach((e: any) => {
          const eqId = e.equipment?.id;
          if (eqId && !mergedFront._equipmentMap.has(eqId)) {
            mergedFront._equipmentMap.set(eqId, e);
            mergedFront.equipmentRows.push(e);
          }
        });

        // Los subcontratos no se reparten por fase: una empresa registrada en el frente se
        // conserva una sola vez, con la clave empresa + tipo de recurso.
        (front.subcontractRows || []).forEach((sub: any) => {
          const key = `${sub.subcontractor?.id}-${sub.resourceType}`;
          if (sub.subcontractor?.id && !mergedFront._subcontractMap.has(key)) {
            mergedFront._subcontractMap.set(key, sub);
            mergedFront.subcontractRows.push(sub);
          }
        });

        (front.productionRows || []).forEach((prod: any) => {
          const tNum = prod.tower?.towerNumber || prod.tower?.id;
          if (!mergedFront._towerMap.has(tNum)) {
            const newProd = JSON.parse(JSON.stringify(prod));
            newProd.matrixProgress = {};
            newProd.phaseIds = {};
            newProd.phaseCumulatives = {};
            newProd.phaseOriginalProgress = {};
            if (newProd.tower && !newProd.tower.originalTowerNumber) {
               newProd.tower.originalTowerNumber = newProd.tower.towerNumber;
            }
            mergedFront._towerMap.set(tNum, newProd);
            mergedFront.productionRows.push(newProd);
          }

          const mergedProd = mergedFront._towerMap.get(tNum);
          mergedProd.matrixProgress[phaseName] = prod.progressPercentage || 0;
          mergedProd.phaseIds[phaseName] = prod.id || 0;
          mergedProd.phaseCumulatives[phaseName] = prod.cumulativePercentage || 0;
          mergedProd.phaseOriginalProgress[phaseName] = prod.progressPercentage || 0;
        });
      });
    });

    mergedActivity.workFronts = Array.from(mergedFrontsMap.values()).map((front: any) => {
      front.productionRows.forEach((prod: any) => {
        let sum = 0; let count = 0;
        Object.values(prod.matrixProgress).forEach((v: any) => { sum += Number(v); count++; });
        prod.progressPercentage = count > 0 ? Number((sum / count).toFixed(2)) : 0;
      });
      delete front._towerMap; delete front._personnelMap; delete front._equipmentMap; delete front._subcontractMap;
      return front;
    });

    pivotedActivities.push(mergedActivity);
  });

  return pivotedActivities;
};

interface DailyReportFormProps {
  projects: Project[];
  onCancel: () => void;
  onAddDailyReport: (
    dailyReport: Omit<DailyReport, 'id'>,
    productionData: DailyReportTower[],
    personnelData: DailyReportEmployee[],
    equipmentData: DailyReportEquipment[],
    activityData: DailyReportActivity[],
    activityTotals: Record<number, { hh: number; hm: number; production: Record<number, number> }>,
    subcontractData?: DailyReportSubcontract[]
  ) => Promise<any>;
  onUpdateDailyReport?: (
    updatedReportData: Omit<DailyReport, 'id'>,
    addedActivities: DailyReportActivity[],
    activityTotals: Record<number, { hh: number; hm: number; production: Record<number, number> }>,
    initialActivities: any[]
  ) => Promise<any>;
  assignedEmployees: Set<string>;
  assignedEquipment: Set<number>;
  onAssignEmployee: (rut: string) => void;
  onAssignEquipment: (id: number) => void;
  unassignEmployee: (rut: string) => void;
  unassignEquipment: (id: number) => void;
  submitDaily?: boolean;
  isEdit?: boolean;
  modifyActivities?: any[];
  reportDataToEdit?: DailyReport | null;
  alertError?: string | null;
  setAlertError?: (value: string | null) => void;
  onRegisterDeletedTower?: (id: number) => void;
  onLoadPreviousData?: (projectId: number) => Promise<void>;
  status?: string;
}

const DailyReportForm: React.FC<DailyReportFormProps> = ({
  projects,
  onAddDailyReport,
  onCancel,
  assignedEmployees,
  assignedEquipment,
  onAssignEmployee,
  onAssignEquipment,
  unassignEmployee,
  unassignEquipment,
  submitDaily,
  isEdit,
  modifyActivities,
  reportDataToEdit,
  onUpdateDailyReport,
  alertError,
  setAlertError,
  onRegisterDeletedTower,
  status
}) => {
  const [reportData, setReportData] = useState<Omit<DailyReport, 'id'>>({
    idProject: 0,
    date: new Date(),
    idProjectActivity: 1,
    observations: '',
    workFront: 0,
    identificationNum: '',
  });

  const [activitiesProject, setActivitiesProject] = useState<ProjectActivity[]>([]);
  const [copyPrevious, setCopyPrevious] = useState(false);
  const [initialActivities, setInitialActivities] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<number>();
  const [employeesList, setEmployees] = useState<Employee[]>([]);
  const [towersList, setTowerData] = useState<Tower[]>([]);
  const [activityTowerRelations, setActivityTowerRelations] = useState<ProjectActivityTowerResponse[]>([]);
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedActivity, setSelectedActivity] = useState<string>('');
  const [uniqueActivities, setUniqueActivities] = useState<string[]>([]);
  const [filteredSubActivities, setFilteredSubActivities] = useState<string[]>([]);
  const [selectedSubActivity, setSelectedSubActivity] = useState<string>('');
  const [addedActivities, setAddedActivities] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const { showMessage } = useSnackbar();

  // El pool de personal por fichaje/geocerca (marca de entrada BUK dentro de la geocerca) es una
  // funcionalidad EXCLUSIVA de Chile: la integración BUK solo existe para ese país. Para el resto
  // de países ('ES', etc.) la marca no aplica y se mantiene el pool clásico = todo el personal del
  // proyecto. Sin este gate, los países sin BUK recibirían una lista vacía y no podrían asignar
  // personal en el reporte diario.
  const { selectedCountry } = useCountry();
  const isBukCountry = selectedCountry?.code === 'CL';

  const [activityTotals, setActivityTotals] = useState<Record<number, { hh: number; hm: number; production: Record<number, number> }>>({});
  const [viewCreate, setViewCreate] = useState<string>();

  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [warningList, setWarningList] = useState<string[]>([]);
  const [isDraftAction, setIsDraftAction] = useState(true);

  const [isDirty, setIsDirty] = useState(false);
  const bypassBlockerRef = useRef(false);

  useEffect(() => {
    if (!isEdit) {
      Array.from(assignedEmployees).forEach(rut => unassignEmployee(rut));
      Array.from(assignedEquipment).forEach(id => unassignEquipment(id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProject || addedActivities.length > 0) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [selectedProject, addedActivities.length]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty && !submitDaily) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, submitDaily]);

  const blocker = useBlocker(
      ({ currentLocation, nextLocation }) =>
        isDirty && !submitDaily && !bypassBlockerRef.current && currentLocation.pathname !== nextLocation.pathname
    );

  const uniqueSections = Array.from(new Set(activitiesProject.map((activity) => activity.activity.section)));
  const displayedError = alertError || alertMessage;

  const handleUpdateTotals = useCallback((index: number, hh: number, hm: number, productionByFront: Record<number, number>) => {
    setActivityTotals((prev) => ({
      ...prev,
      [index]: { hh, hm, production: productionByFront },
    }));
  }, []);

  useEffect(() => {
    if (modifyActivities && modifyActivities.length > 0) {
      const rebuiltActivities = rebuildMatrixActivities(modifyActivities);
      const newAssignedEmployees = new Set<string>();
      const newAssignedEquipment = new Set<number>();

      rebuiltActivities.forEach((activity: any) => {
        if (activity.workFronts) {
          activity.workFronts.forEach((front: any) => {
            (front.personnelRows || []).forEach((person: any) => {
              if (person.employee?.rut) newAssignedEmployees.add(person.employee.rut);
            });
            (front.equipmentRows || []).forEach((equip: any) => {
              if (equip.equipment?.id) newAssignedEquipment.add(equip.equipment.id);
            });
          });
        }
      });

      assignedEmployees.clear();
      newAssignedEmployees.forEach(rut => assignedEmployees.add(rut));
      assignedEquipment.clear();
      newAssignedEquipment.forEach(id => assignedEquipment.add(id));

      setAddedActivities(rebuiltActivities);
      setInitialActivities(JSON.parse(JSON.stringify(rebuiltActivities)));
    }
  }, [modifyActivities]);

  useEffect(() => {
    if (reportDataToEdit) {
      const dateValue = new Date(reportDataToEdit.date);
      const originalDate = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
      setReportData({
        idProject: reportDataToEdit.idProject,
        date: originalDate,
        idProjectActivity: reportDataToEdit.idProjectActivity || 1,
        observations: reportDataToEdit.observations || '',
        workFront: reportDataToEdit.workFront || 0,
        identificationNum: reportDataToEdit.identificationNum || '',
      });
      setSelectedProject(reportDataToEdit.idProject);
    }
  }, [reportDataToEdit]);

  useEffect(() => {
    const fetchStaticActivities = async () => {
      if (!selectedProject) {
        setActivitiesProject([]);
        setTowerData([]);
        setActivityTowerRelations([]);
        return;
      }

      try {
        const [
          activitiesData,
          fetchedTowers,
          activityTowerRelationsData
        ] = await Promise.all([
          getProjectActivities(selectedProject),
          getTowersByProjectId(selectedProject),
          getActivityTowerRelationsByProject(selectedProject)
        ]);

        setActivitiesProject(activitiesData);
        setTowerData(fetchedTowers);
        setActivityTowerRelations(activityTowerRelationsData);
      } catch (error) {
        console.error("Error al cargar actividades, torres o relaciones actividad-torre:", error);
      }
    };

    fetchStaticActivities();
  }, [selectedProject]);

  useEffect(() => {
    const fetchResources = async () => {
      if (selectedProject && reportData.date) {
        try {
          const isDraftReport = reportDataToEdit?.isDraft;
          const dateStr = reportData.date instanceof Date && !isNaN(reportData.date.getTime())
            ? reportData.date.toISOString().substring(0, 10)
            : '';

          if (!isEdit || isDraftReport) {
            // Solo Chile (BUK): pool de personal = quienes FICHARON ese día con el CC del proyecto
            // (marca de entrada + geocerca). Si nadie fichó, la lista viene vacía y no se ofrece
            // personal. Resto de países: pool clásico = todo el personal asignado al proyecto.
            const fetchedEmployees = isBukCountry
              ? (dateStr ? await getFichadosByDate(selectedProject, dateStr) : [])
              : await getEmployeesByIdProject(selectedProject);
            setEmployees(fetchedEmployees);

            const fetchedEquipment = await getEquipmentByIdProject(selectedProject);
            setEquipmentList(fetchedEquipment);
          } else if (dateStr) {
            const fetchedEmployees = await getEmployeesByReportDate(selectedProject, dateStr);
            setEmployees(fetchedEmployees);

            const fetchedEquipment = await getEquipmentsByReportDate(selectedProject, dateStr);
            setEquipmentList(fetchedEquipment);
          }
        } catch (error) {
          console.error('Error al obtener los recursos:', error);
        }
      }
    };
    fetchResources();
  }, [selectedProject, reportData.date, isEdit, reportDataToEdit, isBukCountry]);

  useEffect(() => {
      const fetchLastReportData = async () => {
        if (selectedProject && copyPrevious) {
          try {
            // Las actividades del proyecto se piden acá y no se lee activitiesProject del estado:
            // ese fetch corre en paralelo y puede no haber llegado todavía, con lo que la copia
            // quedaría sin ordenar. El orden tiene que estar aplicado al momento de copiar.
            const [lastReportDetails, projectActivities] = await Promise.all([
              getLastDailyReport(selectedProject),
              getProjectActivities(selectedProject).catch((e) => {
                console.warn("No se pudieron cargar las actividades del proyecto (orden):", e);
                return [];
              })
            ]);

            if (lastReportDetails && lastReportDetails.length > 0) {
              const mappedActivities = lastReportDetails.map((detail: any) => {
                // El nombre vive en activityName o en la actividad maestra (activity.activity.name),
                // NO en detail.activity.name (que no existe en la entidad DailyReportActivity).
                const activityName = (detail.activity?.activityName || detail.activity?.activity?.name || "").trim();
                const isHormigonado = activityName === "Hormigonado";

                return {
                  ...detail.activity,
                  activityDate: reportData.date,
                  dailyReport: { ...reportData, date: reportData.date },
                  id: 0,
                  workFronts: detail.workFrontDetails.map((front: any) => ({
                    frontNumber: front.workFront.frontNumber,
                    productionRows: (front.production || []).map((prod: any) => {
                      const oldHistory = Number(prod.cumulativePercentage ?? prod.cumulative_percentage ?? 0);
                      const yesterdayProgress = Number(prod.progressPercentage ?? prod.progress_percentage ?? 0);
                      const dailyProgress = isHormigonado ? 0 : yesterdayProgress;
                      const newTotalCumulative = oldHistory + dailyProgress;

                      return {
                        ...prod,
                        id: 0,
                        progressPercentage: dailyProgress,
                        advance: 0,
                        realAdvance: 0,
                        cumulativePercentage: newTotalCumulative
                      };
                    }),
                    personnelRows: (front.personnel || []).map((p: any) => ({ ...p, id: 0 })),
                    equipmentRows: (front.equipment || []).map((e: any) => ({ ...e, id: 0 })),
                    subcontractRows: (front.subcontracts || []).map((s: any) => ({ ...s, id: 0 })),
                    observations: front.workFront.observations || ''
                  })),
                  _originalActivityId: detail.activity.activity?.id
                };
              });

              // Se ordena ANTES de fusionar las actividades matriciales (circuito/fase), para que
              // el grupo fusionado quede en la posición que le corresponde según la matriz.
              const orderedActivities = sortActivitiesByProjectOrder(
                mappedActivities,
                buildActivityOrderMap(projectActivities)
              );

              const rebuiltMappedActivities = rebuildMatrixActivities(orderedActivities);

              rebuiltMappedActivities.forEach((activity: any) => {
                activity.workFronts.forEach((front: any) => {
                  (front.personnelRows || []).forEach((p: any) => {
                    if (p.employee?.rut) {
                      setTimeout(() => onAssignEmployee(p.employee.rut), 0);
                    }
                  });
                  (front.equipmentRows || []).forEach((e: any) => {
                    if (e.equipment?.id) {
                      setTimeout(() => onAssignEquipment(e.equipment.id), 0);
                    }
                  });
                });
              });
              setAddedActivities(rebuiltMappedActivities);
            }
          } catch (error) {
            console.error("Error al cargar reporte anterior:", error);
          }
        } else if (!isEdit && !copyPrevious) {
          addedActivities.forEach((activity: any) => {
            activity.workFronts.forEach((front: any) => {
              front.personnelRows?.forEach((p: any) => {
                if (p.employee?.rut) unassignEmployee(p.employee.rut);
              });
              front.equipmentRows?.forEach((e: any) => {
                if (e.equipment?.id) unassignEquipment(e.equipment.id);
              });
            });
          });
          setAddedActivities([]);
        }
      };
      fetchLastReportData();
    }, [copyPrevious, selectedProject]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue) {
      const newDate = new Date(`${inputValue}T00:00:00`);
      if (!isNaN(newDate.getTime())) {
        setReportData((prevData) => ({ ...prevData, date: newDate }));
      }
    }
  };

  const getFormattedDate = (): string => {
    if (reportData.date instanceof Date && !isNaN(reportData.date.getTime())) {
      const d = reportData.date;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
     return '';
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCopyPrevious(event.target.checked);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let newValue = value.replace(/^0+/, '');
    if (newValue === '') newValue = '0';
    setReportData((prevData) => ({ ...prevData, [name]: newValue }));
  };

  const handleProjectChange = (e: SelectChangeEvent<number>) => {
    const projectId = Number(e.target.value);
    setSelectedProject(projectId);
    setError(null);
    setSelectedStage('');
    setSelectedActivity('');
    setSelectedSubActivity('');
    setUniqueActivities([]);
    setReportData((prevData) => ({ ...prevData, idProject: projectId }));
  };

  const handleStageChange = (e: SelectChangeEvent<string>) => {
    setError('');
    const stage = e.target.value;
    setSelectedStage(stage);
    const activitiesForStage = activitiesProject.filter((activity) => activity.activity.section === stage);
    const uniqueActs = Array.from(new Set(activitiesForStage.map((activity) => activity.activity.name))).filter(Boolean) as string[];
    setUniqueActivities(uniqueActs);

    setSelectedActivity('');
    setSelectedSubActivity('');
    setFilteredSubActivities([]);
  };

  const handleActivityChange = useCallback((index: number, updatedActivity: any) => {
    setAddedActivities((prevActivities) => {
      const updatedActivities = [...prevActivities];
      updatedActivities[index] = updatedActivity;
      return updatedActivities;
    });
  }, []);

  const handleSelectActivityChange = (e: SelectChangeEvent<string>) => {
    const selectedActivityName = e.target.value;
    setSelectedActivity(selectedActivityName);

    const isMatrix = selectedActivityName.trim() === "Riega de Piloto Conductor" || selectedActivityName.trim() === "Tendido de Conductor";
    const matchingActivities = activitiesProject.filter((activity) => activity.activity.name === selectedActivityName);

    if (matchingActivities.length > 0) {
      if (isMatrix) {
          setFilteredSubActivities([]);
          setSelectedSubActivity("");
          setReportData((prevData) => ({ ...prevData, idProjectActivity: matchingActivities[0].id }));
      } else {
          const uniqueSubs = Array.from(new Set(matchingActivities.map((a) => a.activity.subActivity).filter(Boolean))) as string[];
          setFilteredSubActivities(uniqueSubs);
          if (uniqueSubs.length === 1) {
            setSelectedSubActivity(uniqueSubs[0]);
            setReportData((prevData) => ({ ...prevData, idProjectActivity: matchingActivities[0].id }));
          } else {
            setSelectedSubActivity("");
          }
      }
    }
  };

  const handleSelectSubActivityChange = (e: SelectChangeEvent<string>) => {
    const sub = e.target.value;
    setSelectedSubActivity(sub);
    const obj = activitiesProject.find((a) => a.activity.name === selectedActivity && a.activity.subActivity === sub);
    if (obj) setReportData((prevData) => ({ ...prevData, idProjectActivity: obj.id }));
  };

  const addActivity = () => {
    setViewCreate('create');
    if (!selectedProject || !selectedActivity) {
      setError("Debe seleccionar un proyecto y una actividad.");
      return;
    }
    setError(null);

    const isMatrix = selectedActivity.trim() === "Riega de Piloto Conductor" || selectedActivity.trim() === "Tendido de Conductor";
    let targetObj: ProjectActivity | undefined;

    if (isMatrix) {
      targetObj = activitiesProject.find((a) => a.activity.name === selectedActivity);
    } else {
      const obj = activitiesProject.find((a) => a.activity.name === selectedActivity && a.activity.subActivity === selectedSubActivity);
      targetObj = obj || activitiesProject.find((a) =>
        a.activity.name === selectedActivity &&
        a.activity.subActivity?.toLowerCase().includes(selectedSubActivity.toLowerCase())
      );
    }

    if (filteredSubActivities.length > 0 && !selectedSubActivity) {
      setError("Debe seleccionar una subactividad para poder agregar.");
      return;
    }

    if (!targetObj) {
      setError("No se encontró la configuración de la actividad. Verifique sus selecciones.");
      return;
    }

    const existingIndex = addedActivities.findIndex(a =>
          a.activity.id === targetObj!.activity.id ||
          (a.activityName === targetObj!.activity.name && a.activity.subActivity === targetObj!.activity.subActivity)
        );

    const quantityToAdd = reportData.workFront > 0 ? reportData.workFront : 1;

    if (existingIndex !== -1) {
      const updatedActivities = [...addedActivities];
      const targetActivity = updatedActivities[existingIndex];
      const currentFrontsCount = targetActivity.workFronts.length;

      const newFronts = Array.from({ length: quantityToAdd }, (_, i) => ({
        frontNumber: currentFrontsCount + 1 + i,
        productionRows: [],
        personnelRows: [],
        equipmentRows: [],
        subcontractRows: [],
        observations: '',
        totalHh: 0,
        totalHm: 0,
      }));

      targetActivity.workFronts = [...targetActivity.workFronts, ...newFronts];
      updatedActivities[existingIndex] = targetActivity;

      setAddedActivities(updatedActivities);
      showMessage(`Se agregó un nuevo frente a la actividad existente: ${targetObj.activity.name}`, "info");

    } else {
      const newActivity: DailyReportActivity = {
        activityName: targetObj.activity.name || "",
        workFront: reportData.workFront,
        activityDate: reportData.date,
        dailyReport: reportData as any,
        activity: { ...targetObj.activity, subActivity: selectedSubActivity },
        idDependencyAct: targetObj.activityIdDependency,
        projectActivityId: targetObj.id,
        activityTower: targetObj.activityTower
      } as any;

      const workFronts = Array.from({ length: quantityToAdd }, (_, i) => ({
        frontNumber: i + 1,
        productionRows: [],
        personnelRows: [],
        equipmentRows: [],
        subcontractRows: [],
        observations: '',
        totalHh: 0,
        totalHm: 0,
      }));
      setAddedActivities((prev) => [...prev, { ...newActivity, workFronts }]);
    }
  };

  const handleRemoveActivity = (index: number) => {
    const act = addedActivities[index];
    act.workFronts.forEach((f: any) => {
      f.personnelRows.forEach((p: any) => unassignEmployee(p.employee.rut));
      f.equipmentRows.forEach((eq: any) => unassignEquipment(eq.equipment.id));
    });
    setAddedActivities((prev) => prev.filter((_, i) => i !== index));
  };

  const isValidDate = (date: Date): boolean => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;
    const year = date.getFullYear();
    return year >= 2000 && year <= 2100;
  };

  const toLocalISOString = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
  };

  const handleCancelClick = () => {
    setIsDirty(false);
    if (!isEdit) {
      Array.from(assignedEmployees).forEach(rut => unassignEmployee(rut));
      Array.from(assignedEquipment).forEach(id => unassignEquipment(id));
    }
    onCancel();
  };

  const handleSubmit = async (asDraft: boolean = false) => {
    if (submitDaily) return;
    setAlertMessage(null);
    setIsDraftAction(asDraft);

    if (!isValidDate(reportData.date)) {
      setAlertMessage("La fecha del reporte no es válida.");
      return;
    }
    if (!selectedProject) {
      setError("Seleccione un proyecto.");
      return;
    }
    if (addedActivities.length === 0) {
      setActivityError("Agregue al menos una actividad.");
      return;
    }

    let missingPersonnelMsg: string | null = null;
    if (employeesList.length > 0) {
      const unassignedPersonnel = employeesList.filter(emp => emp.rut && !assignedEmployees.has(emp.rut));
      if (unassignedPersonnel.length > 0) {
        const missingNames = unassignedPersonnel.map(nombrePersonal).join(", ");
        const alertMsg = `Todo personal debe estar cargado a alguna actividad. Falta asignar: ${missingNames}`;
        if (!asDraft) {
          setAlertMessage(`⚠️ ALERTA: ${alertMsg}`);
          return;
        } else {
          missingPersonnelMsg = alertMsg;
        }
      }
    }

    let missingEquipmentMsg: string | null = null;
    if (equipmentList.length > 0) {
      const unassignedEquipment = equipmentList.filter(eq => eq.id && !assignedEquipment.has(eq.id));
      if (unassignedEquipment.length > 0) {
        const missingNames = unassignedEquipment.map(etiquetaEquipo).join(", ");
        const alertMsg = `Todo equipo debe estar cargado a alguna actividad. Falta asignar: ${missingNames}`;
        if (!asDraft) {
          setAlertMessage(`⚠️ ALERTA: ${alertMsg}`);
          return;
        } else {
          missingEquipmentMsg = alertMsg;
        }
      }
    }

    let missingReasonMsg: string | null = null;
    addedActivities.forEach((activity: any) => {
      activity.workFronts.forEach((front: any) => {
        (front.productionRows || []).forEach((prod: any) => {
          if ((!prod.progressPercentage || prod.progressPercentage === 0) && !prod.zeroProgressReason) {
             missingReasonMsg = `Todo avance en 0 debe tener un motivo. Revise la torre ${prod.tower?.towerNumber || prod.tower?.originalTowerNumber || ''} en la actividad '${activity.activityName}'.`;
          }
        });
      });
    });

    if (missingReasonMsg) {
      setAlertMessage(`⚠️ ALERTA: ${missingReasonMsg}`);
      return;
    }

    try {
      const safeDateString = toLocalISOString(reportData.date);

      const expandedActivitiesRaw: any[] = [];
      const expandedActivityTotals: Record<number, any> = {};

      addedActivities.forEach((activity, index) => {
        const actName = activity.activityName?.trim();
        const isMatrix = actName === "Riega de Piloto Conductor" || actName === "Tendido de Conductor";

        if (isMatrix) {
          const matchingPhases = activitiesProject.filter(a =>
           a.activity.name?.trim() === actName &&
           a.activity.subActivity?.toLowerCase().includes("fase")
          );

          const numPhases = matchingPhases.length;

          if (numPhases > 0) {
            matchingPhases.forEach((phaseObj, phaseIndex) => {
              const phaseName = phaseObj.activity.subActivity || "";
              const phaseActivity = JSON.parse(JSON.stringify(activity));
              phaseActivity.activity = phaseObj.activity;
              phaseActivity.idDependencyAct = phaseObj.activityIdDependency;
              const targetPhaseNorm = phaseName.trim().toLowerCase();
              let trueActivityId = 0;
              if (activity._phaseActivityIds) {
                  for (const k in activity._phaseActivityIds) {
                      if (k.trim().toLowerCase() === targetPhaseNorm) {
                          trueActivityId = activity._phaseActivityIds[k];
                          break;
                      }
                  }
              }

              phaseActivity.id = isEdit && trueActivityId > 0 ? trueActivityId : 0;

             phaseActivity.workFronts = phaseActivity.workFronts.map((front: any) => {
               const phaseWeights = matchingPhases.map(pObj => {
                   const pName = (pObj.activity.subActivity || "").trim().toLowerCase();
                   return (front.productionRows || []).reduce((sum: number, prod: any) => {
                       let matchProgress = 0;
                       if (prod.matrixProgress) {
                           for (const k in prod.matrixProgress) {
                               if (k.trim().toLowerCase() === pName) {
                                   matchProgress = Number(prod.matrixProgress[k]) || 0;
                                   break;
                               }
                           }
                       }
                       return sum + matchProgress;
                   }, 0);
               });

               const totalFrontProgress = phaseWeights.reduce((a, b) => a + b, 0);
               const currentPhaseWeight = phaseWeights[phaseIndex];
               const ratio = totalFrontProgress > 0 ? (currentPhaseWeight / totalFrontProgress) : (1 / numPhases);

               const distributedPersonnel = distributeProportionally(front.personnelRows || [], phaseWeights);
               const distributedEquipment = distributeProportionally(front.equipmentRows || [], phaseWeights);

               front.personnelRows = distributedPersonnel[phaseIndex] || [];
               front.equipmentRows = distributedEquipment[phaseIndex] || [];

               front.totalProduction = Number(((front.totalProduction || 0) * ratio).toFixed(2));
               front.totalHh = Number(((front.totalHh || 0) * ratio).toFixed(2));
               front.totalHm = Number(((front.totalHm || 0) * ratio).toFixed(2));

               front.productionRows = (front.productionRows || []).map((prod: any) => {
                 let specificPhaseProgress = 0;
                 if (prod.matrixProgress) {
                     for (const k in prod.matrixProgress) {
                         if (k.trim().toLowerCase() === targetPhaseNorm) {
                             specificPhaseProgress = Number(prod.matrixProgress[k]) || 0;
                             break;
                         }
                     }
                 }

                 let phaseId = 0;
                 if (prod.phaseIds) {
                     for (const k in prod.phaseIds) {
                         if (k.trim().toLowerCase() === targetPhaseNorm) {
                             phaseId = prod.phaseIds[k] || 0;
                             break;
                         }
                     }
                 }

                 let oldCumulative = prod.cumulativePercentage || 0;
                 if (prod.phaseCumulatives) {
                     for (const k in prod.phaseCumulatives) {
                         if (k.trim().toLowerCase() === targetPhaseNorm) {
                             oldCumulative = prod.phaseCumulatives[k] || 0;
                             break;
                         }
                     }
                 }

                 let oldProgress = prod.progressPercentage || 0;
                 if (prod.phaseOriginalProgress) {
                     for (const k in prod.phaseOriginalProgress) {
                         if (k.trim().toLowerCase() === targetPhaseNorm) {
                             oldProgress = prod.phaseOriginalProgress[k] || 0;
                             break;
                         }
                     }
                 }

                 const forwardSpan = getForwardSpan(prod.tower);
                 const vanoAdelanteKm = forwardSpan / 1000;

                 const historicalBase = oldCumulative - oldProgress;
                 const specificPhaseCumulative = historicalBase + specificPhaseProgress;

                 const calculatedAdvance = (specificPhaseProgress / 100) * vanoAdelanteKm;
                 const calculatedCumulativeAdvance = (specificPhaseCumulative / 100) * vanoAdelanteKm;

                 return {
                   ...prod,
                   id: isEdit ? phaseId : 0,
                   progressPercentage: specificPhaseProgress,
                   cumulativePercentage: specificPhaseCumulative,
                   advance: Number(calculatedAdvance.toFixed(5)),
                   cumulativeAdvance: Number(calculatedCumulativeAdvance.toFixed(5))
                 };
               }).filter((prod: any) => prod.progressPercentage > 0 || prod.id > 0 || prod.zeroProgressReason);

               return front;
             }).filter((front: any) => {
               const hasProgress = front.productionRows.length > 0;
               return front.personnelRows.length > 0 || front.equipmentRows.length > 0 || hasProgress;
             });

             if (phaseActivity.workFronts.length > 0) {
                 const newIndex = expandedActivitiesRaw.length;
                 expandedActivitiesRaw.push(phaseActivity);

                 const origTotal = activityTotals[index];
                 if (origTotal) {
                     let phaseTotalProgress = 0;
                     let allPhasesTotalProgress = 0;

                     activity.workFronts.forEach((f: any) => {
                          (f.productionRows || []).forEach((prod: any) => {
                              const targetPhaseNorm = phaseName.trim().toLowerCase();
                              if (prod.matrixProgress) {
                                  for (const k in prod.matrixProgress) {
                                      if (k.trim().toLowerCase() === targetPhaseNorm) phaseTotalProgress += Number(prod.matrixProgress[k]) || 0;
                                  }
                              }

                              matchingPhases.forEach(pObj => {
                                  const pName = (pObj.activity.subActivity || "").trim().toLowerCase();
                                  if (prod.matrixProgress) {
                                      for (const k in prod.matrixProgress) {
                                          if (k.trim().toLowerCase() === pName) allPhasesTotalProgress += Number(prod.matrixProgress[k]) || 0;
                                      }
                                  }
                              });
                          });
                     });

                     const activityRatio = allPhasesTotalProgress > 0 ? (phaseTotalProgress / allPhasesTotalProgress) : (1 / numPhases);

                     const newProdMap: Record<number, number> = {};
                     if (origTotal.production) {
                         Object.keys(origTotal.production).forEach(fn => {
                             newProdMap[Number(fn)] = Number((origTotal.production[Number(fn)] * activityRatio).toFixed(2));
                         });
                     }
                     expandedActivityTotals[newIndex] = {
                         hh: Number(((origTotal.hh || 0) * activityRatio).toFixed(2)),
                         hm: Number(((origTotal.hm || 0) * activityRatio).toFixed(2)),
                         production: newProdMap
                     };
                 }
             }
            });
          } else {
             const newIndex = expandedActivitiesRaw.length;
             expandedActivitiesRaw.push(activity);
             expandedActivityTotals[newIndex] = activityTotals[index];
          }
        } else {
          const newIndex = expandedActivitiesRaw.length;
          expandedActivitiesRaw.push(activity);
          expandedActivityTotals[newIndex] = activityTotals[index];
        }
      });

      const sanitizedActivities = expandedActivitiesRaw.map((activity, index) => {
        const activityForRows = {
          id: isEdit ? (activity.id || 0) : 0,
          activityName: activity.activityName,
          activity: activity.activity
        };

        return {
          ...activity,
          id: isEdit ? (activity.id || null) : null,
          activityDate: safeDateString,
          dailyReport: { ...reportData, date: safeDateString },
          workFronts: activity.workFronts.map((front: any) => {

            const calculatedProd = expandedActivityTotals[index]?.production?.[front.frontNumber] ?? front.totalProduction ?? 0;
            const calculatedHh = expandedActivityTotals[index]?.hh ?? front.totalHh ?? 0;
            const calculatedHm = expandedActivityTotals[index]?.hm ?? front.totalHm ?? 0;

            const masterWorkFront = {
              id: isEdit ? (front.id || null) : null,
              frontNumber: front.frontNumber,
              observations: front.observations || '',
              totalProduction: calculatedProd,
              totalHh: calculatedHh,
              totalHm: calculatedHm
            };

            const fixRow = (row: any) => {
              const { workFront, dailyReportActivity, dailyReport, matrixProgress, phaseIds, phaseCumulatives, phaseOriginalProgress, ...rest } = row;
              return {
                ...rest,
                id: isEdit ? row.id : 0,
                workFront: masterWorkFront,
                dailyReportActivity: activityForRows,
                dailyReport: null
              };
            };

            return {
              ...front,
              id: isEdit ? front.id : null,
              totalProduction: calculatedProd,
              totalHh: calculatedHh,
              totalHm: calculatedHm,
              productionRows: (front.productionRows || []).map(fixRow).filter((prod: any) => prod.progressPercentage > 0 || prod.id > 0 || prod.zeroProgressReason),
              personnelRows: (front.personnelRows || []).map(fixRow),
              equipmentRows: (front.equipmentRows || []).map(fixRow),
              subcontractRows: (front.subcontractRows || []).map(fixRow)
            };
          })
        };
      });

      const productionData = sanitizedActivities.flatMap(a => a.workFronts.flatMap((f: any) => f.productionRows));
      const personnelData = sanitizedActivities.flatMap(a => a.workFronts.flatMap((f: any) => f.personnelRows));
      const equipmentData = sanitizedActivities.flatMap(a => a.workFronts.flatMap((f: any) => f.equipmentRows));
      const subcontractData = sanitizedActivities.flatMap(a => a.workFronts.flatMap((f: any) => f.subcontractRows || []));

      const reportPayload = {
        ...reportData,
        date: safeDateString,
        ...(isEdit && reportDataToEdit?.id ? { id: reportDataToEdit.id } : {}),
        isDraft: asDraft
      };

      let result: any = null;

      if (isEdit && reportDataToEdit && onUpdateDailyReport) {
        result = await onUpdateDailyReport(reportPayload as any, sanitizedActivities, expandedActivityTotals, initialActivities);
      } else {
        result = await onAddDailyReport(reportPayload as any, productionData, personnelData, equipmentData, sanitizedActivities, expandedActivityTotals, subcontractData);
      }

      if (result && typeof result === 'object') {
        let combinedWarnings = result.warnings || [];

        if (missingPersonnelMsg) combinedWarnings.push(missingPersonnelMsg);
        if (missingEquipmentMsg) combinedWarnings.push(missingEquipmentMsg);

        if (combinedWarnings.length > 0) {
          setWarningList(combinedWarnings);
          setWarningModalOpen(true);
          return;
        }

        bypassBlockerRef.current = true;
        setIsDirty(false);
        showMessage(asDraft ? "Borrador guardado" : "Reporte procesado exitosamente", "success");

        if (!isEdit) {
            Array.from(assignedEmployees).forEach(rut => unassignEmployee(rut));
            Array.from(assignedEquipment).forEach(id => unassignEquipment(id));
        }

        onCancel();
      }

    } catch (err: any) {
      console.error("Error en submit:", err);
      let msg = "Error al procesar la solicitud.";
      if (err.response?.data) msg = err.response.data.mensaje || err.response.data.error || msg;
      // Los errores de varias lineas (como el listado de recursos ya asignados a otro proyecto) van
      // a la alerta del formulario, que permanece hasta que el usuario la cierra. El snackbar general
      // se oculta a los pocos segundos y no da tiempo a leer una lista ni a anotarla.
      if (/\r?\n/.test(msg)) {
        setAlertMessage(msg);
      } else {
        showMessage(msg, "error");
      }
    }
  };

  const handleCloseWarningModal = () => {
    bypassBlockerRef.current = true;
    setIsDirty(false);
    setWarningModalOpen(false);
    onCancel();
  };

  if (isEdit && (!reportDataToEdit || !selectedProject || !reportData.date) && addedActivities) {
    return <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>;
  }

  const isViewOnly = !isEdit && status === 'view';

  const getAvailableTowersForActivity = (activity: any): Tower[] => {
  if (!activity?.activityTower) {
    return towersList;
  }

  const projectActivityId = Number(activity.projectActivityId);

  if (!projectActivityId) {
    return [];
  }

  const allowedTowerNumbers = new Set(
    activityTowerRelations
      .filter(relation =>
        relation.projectActivityId === projectActivityId &&
        relation.active
      )
      .map(relation => String(relation.towerNumber).trim())
  );

  return towersList.filter(tower =>
    allowedTowerNumbers.has(String(tower.towerNumber).trim())
  );
};

  return (
    <>
      <Snackbar open={Boolean(displayedError)} autoHideDuration={10000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={error ? "error" : "warning"} variant="filled" sx={{ whiteSpace: 'pre-line' }}
          action={<IconButton color="inherit" size="small" onClick={() => alertError ? setAlertError?.(null) : setAlertMessage(null)}><CloseIcon fontSize="inherit" /></IconButton>}>
          {displayedError}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>Información general</Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Proyecto</InputLabel>
              <Select label="Proyecto" value={selectedProject || ''} onChange={handleProjectChange} disabled={isEdit || isViewOnly}>
                {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Día de reporte" type="date" InputLabelProps={{ shrink: true }} value={getFormattedDate()} onChange={handleDateChange} fullWidth disabled={isEdit || isViewOnly} />
          </Grid>
          {selectedProject && !isViewOnly && <Grid item xs={12} sm={3}><FormControlLabel control={<Checkbox checked={copyPrevious} onChange={handleCheckboxChange} disabled={isEdit} />} label="Copiar día anterior" /></Grid>}
        </Grid>

        {selectedProject && !isViewOnly && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2}><FormControl fullWidth><InputLabel>Etapa</InputLabel><Select value={selectedStage} onChange={handleStageChange}>{uniqueSections.map((s, i) => <MenuItem key={i} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={2}><FormControl fullWidth><InputLabel>Actividad</InputLabel><Select value={selectedActivity} onChange={handleSelectActivityChange}>{uniqueActivities.map((a, i) => <MenuItem key={i} value={a}>{a}</MenuItem>)}</Select></FormControl></Grid>
            {filteredSubActivities.length > 0 && (
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Subactividad</InputLabel>
                  <Select value={selectedSubActivity} onChange={handleSelectSubActivityChange} label="Subactividad">
                    {filteredSubActivities.map((sub, i) => (
                      <MenuItem key={i} value={sub}>{sub}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={2}><TextField label="Frentes" type="number" value={reportData.workFront} onChange={handleInputChange} name="workFront" fullWidth /></Grid>
            <Grid item xs={12} sm={3} container justifyContent="flex-end"><Button variant="contained" onClick={addActivity}>Agregar Actividad</Button></Grid>
          </Grid>
        )}

        {addedActivities.length > 0 && (
          <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Actividades ingresadas:</Typography>
            {addedActivities.map((a, i) => (
              <ActivityDetails
                key={i}
                index={i}
                projectActivities={activitiesProject}
                activity={a}
                workFronts={a.workFronts}
                towers={getAvailableTowersForActivity(a)}
                employees={employeesList}
                equipmentList={equipmentList}
                project={selectedProject}
                assignedEmployees={assignedEmployees}
                assignedEquipment={assignedEquipment}
                onAssignEmployee={onAssignEmployee}
                onAssignEquipment={onAssignEquipment}
                unassignEmployee={unassignEmployee}
                unassignEquipment={unassignEquipment}
                onChange={handleActivityChange}
                onRemove={() => handleRemoveActivity(i)}
                onUpdateTotals={handleUpdateTotals}
                status={isViewOnly ? 'view' : viewCreate}
                isEdit={isEdit}
                reportDate={reportData.date}
                onRegisterDeletedTower={onRegisterDeletedTower}
              />
            ))}
          </Box>
        )}

        <Grid container justifyContent="flex-end" sx={{ mt: 4 }}>
          {!isViewOnly && (
            <>
              <Button variant="contained" onClick={() => handleSubmit(true)} disabled={submitDaily || !selectedProject} sx={{ mr: 2, bgcolor: '#ed6c02', '&:hover': { bgcolor: '#e65100' } }}>
                {submitDaily ? "Guardando..." : "Guardar Borrador"}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleSubmit(false)} disabled={submitDaily || !selectedProject}>
                {submitDaily ? "Guardando..." : isEdit ? "Actualizar / Finalizar" : "Finalizar Reporte"}
              </Button>
            </>
          )}
          <Button variant="text" onClick={handleCancelClick} sx={{ ml: 2 }} disabled={submitDaily}>{isViewOnly ? "Volver" : "Cancelar"}</Button>
        </Grid>
      </Box>

      <Dialog
        open={warningModalOpen}
        onClose={handleCloseWarningModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff7e6', color: '#d46b08' }}>
          {isDraftAction ? <CheckCircleIcon sx={{ mr: 1, color: '#52c41a' }} /> : <WarningAmberIcon sx={{ mr: 1, color: '#fa8c16' }} />}
          {isDraftAction ? "Borrador Guardado con Observaciones" : "Advertencias de Validación"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            {isDraftAction
              ? "El reporte se ha guardado correctamente como Borrador, pero se detectaron las siguientes inconsistencias que deben corregirse antes de Finalizar:"
              : "El reporte fue procesado, pero el sistema detectó las siguientes alertas de actividades precedentes o recursos faltantes:"}
          </DialogContentText>
          <List dense sx={{ bgcolor: '#fff1f0', borderRadius: 1, border: '1px solid #ffccc7' }}>
            {warningList.map((warning, index) => (
              <ListItem key={index}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <WarningAmberIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={warning} primaryTypographyProps={{ variant: 'body2', color: 'error.main', style: { whiteSpace: 'pre-line' } }} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseWarningModal} variant="contained" color="primary" autoFocus>
            Entendido, volver al listado
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff7e6', color: '#d46b08' }}>
          <WarningAmberIcon sx={{ mr: 1, color: '#fa8c16' }} />
          Cambios sin guardar
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Estás editando o creando un reporte. Si navegas a otro módulo ahora, perderás todos los cambios no guardados. ¿Deseas abandonar la página?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => blocker.reset?.()} color="primary" variant="outlined">
            Quedarme
          </Button>
          <Button
            onClick={() => {
              setIsDirty(false);
              blocker.proceed?.();
            }}
            color="error"
            variant="contained"
          >
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DailyReportForm;